import React from 'react';
import { ChevronDown } from 'lucide-react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

const CustomSelect = React.memo(({ item }) => {
    const { name, label, id, value, onChange, options = [], placeholder, required, hint, disabled, error } = item;

    return (
        <div>
            <label htmlFor={name} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {label}
            </label>
            <div className="relative">
                <select
                    id={id}
                    name={name}
                    value={value || ''}
                    onChange={(e) => onChange(name, e.target.value)}
                    disabled={disabled}
                    required={required}
                    className={clsx(
                        'w-full px-3 py-2 pr-10',
                        'border border-gray-300 dark:border-gray-600',
                        'rounded-lg',
                        'appearance-none',
                        'focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400',
                        'bg-white dark:bg-gray-700',
                        'text-gray-900 dark:text-white',
                        '[&>option]:bg-white [&>option]:dark:bg-gray-700',
                        '[&>option]:py-2 [&>option]:px-3',
                        '[&>option:hover]:bg-blue-50 [&>option:hover]:dark:bg-gray-600',
                        '[&>option:checked]:bg-blue-50 [&>option:checked]:dark:bg-gray-600',
                        '[&>option:disabled]:text-gray-400 [&>option:disabled]:dark:text-gray-500',
                        '[&>option:disabled]:cursor-not-allowed',
                        disabled && 'opacity-50 cursor-not-allowed',
                        error && 'border-red-500 dark:border-red-400'
                    )}
                    style={{
                        WebkitAppearance: 'none',
                        MozAppearance: 'none',
                        textIndent: 1,
                        textOverflow: ''
                    }}
                >
                    {placeholder && (
                        <option value="" disabled className={clsx('text-gray-500 dark:text-gray-400', 'italic')}>
                            {placeholder}
                        </option>
                    )}
                    {options.map((option) => (
                        <option
                            key={option.value}
                            value={option.value}
                            disabled={option.disabled}
                            className={clsx('py-2 px-3', 'cursor-pointer', option.disabled && 'text-gray-400 dark:text-gray-500 cursor-not-allowed', !option.disabled && 'hover:bg-blue-50 dark:hover:bg-gray-600')}
                        >
                            {option.label}
                        </option>
                    ))}
                </select>

                <div className={clsx('absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none', 'transition-transform duration-200', 'group-focus-within:transform group-focus-within:rotate-180')}>
                    <ChevronDown className={clsx('h-5 w-5', 'text-gray-400 dark:text-gray-500', disabled && 'opacity-50')} />
                </div>
            </div>

            {(hint || error) && <p className={clsx('mt-1 text-xs', error ? 'text-red-500 dark:text-red-400' : 'text-gray-500 dark:text-gray-400')}>{error || hint}</p>}

            <style jsx>{`
                select {
                    -moz-padding-start: calc(0.75rem - 3px);
                }
                select::-moz-focus-inner {
                    border: 0;
                }
                @-moz-document url-prefix() {
                    select {
                        color-scheme: light dark;
                        padding-right: 2rem;
                    }
                }
            `}</style>
        </div>
    );
});

CustomSelect.displayName = 'CustomSelect';

CustomSelect.propTypes = {
    item: PropTypes.shape({
        name: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        id: PropTypes.string.isRequired,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        onChange: PropTypes.func.isRequired,
        options: PropTypes.arrayOf(
            PropTypes.shape({
                value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
                label: PropTypes.string.isRequired,
                disabled: PropTypes.bool
            })
        ).isRequired,
        placeholder: PropTypes.string,
        required: PropTypes.bool,
        hint: PropTypes.string,
        disabled: PropTypes.bool,
        error: PropTypes.string
    }).isRequired
};

export default CustomSelect;
